import { create } from 'zustand';

import type {
  ApiError,
  ChangePasswordRequest,
  Channel,
  CheckoutsInvoiceRetrieveResponse,
  ConfirmResetRequestWritable,
  OnboardRequestWritable,
  PatchedProfileUpdateRequest,
  ProfileRead,
  ResetPasswordSerialzierRequest,
  StripeCheckoutSuccess,
} from '@double.us/api';
import { assets } from '@double.us/core/config/absolute-url';

import { client, token } from '~/services/client';

import { _upload } from './video';

// ApiKey type - defined locally since it's not exported from the API package
// This matches the structure returned by accountsApiKeysList
export interface ApiKey {
  readonly id: string;
  name: string;
  readonly plain_text: string;
  readonly created_at: Date;
  readonly updated_at: Date;
}

const AUTH_REQUEST_KEYS = [
  'login',
  'register',
  'forgotPassword',
  'resetPassword',
  'verifyEmail',
  'updateUserProfile',
  'changePassword',
  'getBillingHistory',
  'getInvoice',
] as const;

export type AuthRequestKey = (typeof AUTH_REQUEST_KEYS)[number];

export interface AuthRequestStatus {
  loading: boolean;
  error: ApiError | null;
  success: boolean;
}

export type AuthRequestsState = Record<AuthRequestKey, AuthRequestStatus>;

const createDefaultRequestStatus = (): AuthRequestStatus => ({
  loading: false,
  error: null,
  success: false,
});

const createInitialRequestState = (): AuthRequestsState =>
  AUTH_REQUEST_KEYS.reduce((acc, key) => {
    acc[key] = createDefaultRequestStatus();
    return acc;
  }, {} as AuthRequestsState);

const createFallbackApiError = (message: string): ApiError => ({
  type: 'client_error',
  errors: [
    {
      code: 'client_error',
      detail: message,
      attr: null,
    },
  ],
});

// Login request type (not in generated SDK, using direct HTTP call)
interface LoginRequest {
  username: string;
  password: string;
}

export interface AuthState {
  // data
  user: ProfileRead | null;
  channel: Channel | null;
  token: string | null;
  apiKeys: ApiKey[];
  billingHistory: StripeCheckoutSuccess[];
  invoice: CheckoutsInvoiceRetrieveResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isOrganizationLoading: boolean;
  isApiKeysLoading: boolean;
  apiKeysFetched: boolean;
  error: ApiError | null;
  requests: AuthRequestsState;
  // actions
  login: (data: LoginRequest) => Promise<void>;
  register: (data: OnboardRequestWritable) => Promise<void>;
  forgotPassword: (data: ResetPasswordSerialzierRequest) => Promise<void>;
  resetPassword: (data: ConfirmResetRequestWritable) => Promise<void>;
  getUserProfile: (silent?: boolean) => Promise<ProfileRead | undefined>;
  getOrganization: () => Promise<void>;
  getApiKeys: () => Promise<void>;
  getBillingHistory: (channel_id: string) => Promise<void>;
  getInvoice: (invoiceId: string) => Promise<void>;
  verifyEmail: (token: string | null, channel_id: string, user_id: string) => Promise<void>;
  updateUserProfile: (data: PatchedProfileUpdateRequest) => Promise<void>;
  uploadProfilePic: (file: File, payload?: PatchedProfileUpdateRequest) => Promise<void>;
  changePassword: (data: ChangePasswordRequest) => Promise<void>;
  logout: () => void;
  clearRequestState: (request?: AuthRequestKey) => void;
}
export const useAuthStore = create<AuthState>((set, get) => {
  const updateRequestState = (request: AuthRequestKey, partial: Partial<AuthRequestStatus>) => {
    set(state => ({
      requests: {
        ...state.requests,
        [request]: {
          ...state.requests[request],
          ...partial,
        },
      },
    }));
  };

  const resetRequestState = (request?: AuthRequestKey) => {
    if (!request) {
      set({ requests: createInitialRequestState() });
      return;
    }
    set(state => ({
      requests: {
        ...state.requests,
        [request]: createDefaultRequestStatus(),
      },
    }));
  };

  const login = async (data: LoginRequest) => {
    set({ isLoading: true, error: null });
    updateRequestState('login', { loading: true, error: null, success: false });
    const response = await client.accountsGetTokenCreate({ body: data });

    if (response.error) {
      token.clear();
      set({
        error: response.error,
        isAuthenticated: false,
        token: null,
        user: null,
        channel: null,
        isLoading: false,
      });
      updateRequestState('login', { loading: false, error: response.error, success: false });
      return;
    }

    // Set token and authentication status.
    // The PrivateRoute will now be responsible for fetching the user profile.
    token.set(response.data.token);
    set({
      token: response.data.token,
      isAuthenticated: true,
      error: null,
      isLoading: false,
    });
    updateRequestState('login', { loading: false, error: null, success: true });
  };

  const register = async (data: OnboardRequestWritable) => {
    set({ isLoading: true, error: null });
    updateRequestState('register', { loading: true, error: null, success: false });
    const response = await client.onboardCreate({ body: data });
    if (response.error) {
      set({ error: response.error, isLoading: false });
      updateRequestState('register', { loading: false, error: response.error, success: false });
      return;
    }
    set({ isLoading: false });
    updateRequestState('register', { loading: false, error: null, success: true });
  };

  const forgotPassword = async (data: ResetPasswordSerialzierRequest) => {
    set({ isLoading: true, error: null });
    updateRequestState('forgotPassword', { loading: true, error: null, success: false });
    const response = await client.accountsResetPasswordCreate({ body: data });
    if (response.error) {
      set({ error: response.error, isLoading: false });
      updateRequestState('forgotPassword', { loading: false, error: response.error, success: false });
      return;
    }
    set({ isLoading: false });
    updateRequestState('forgotPassword', { loading: false, error: null, success: true });
  };

  const resetPassword = async (data: ConfirmResetRequestWritable) => {
    set({ isLoading: true, error: null });
    updateRequestState('resetPassword', { loading: true, error: null, success: false });
    const response = await client.accountsResetPasswordConfirmCreate({
      body: data,
    });
    if (response.error) {
      set({ error: response.error, isLoading: false });
      updateRequestState('resetPassword', { loading: false, error: response.error, success: false });
      return;
    }
    set({ isLoading: false });
    updateRequestState('resetPassword', { loading: false, error: null, success: true });
  };

  // Password change endpoint is not available in the generated SDK
  // This function is kept for interface compatibility but will need to be implemented
  // when the endpoint is added to the API schema

  const getUserProfile = async (silent = false) => {
    if (!get().token) return;
    if (!silent) {
      set({ isLoading: true, error: null });
    }
    const response = await client.profileRetrieve();
    if (response.error) {
      token.clear();
      set({
        error: response.error,
        isAuthenticated: false,
        user: null,
        channel: null,
        token: null,
      });
    } else {
      // accountsProfileList returns Array<Profile>, get the first one
      const profile = Array.isArray(response.data) ? response.data[0] : response.data;
      set({
        user: profile,
        channel: profile?.channel ?? null,
        isAuthenticated: true,
      });
    }
    if (!silent) {
      set({ isLoading: false });
    }

    const profile: ProfileRead = Array.isArray(response.data) ? response.data[0] : response.data;

    return profile;
  };

  const getOrganization = async () => {
    if (!get().token) return;
    set({ isOrganizationLoading: true, error: null });
    // Organization/channel is now part of the Profile response
    // Fetch profile if not already loaded
    if (!get().user) {
      await getUserProfile();
    }
    // Channel should already be set from getUserProfile
    set({ isOrganizationLoading: false });
  };

  // const updateOrganization = async (orgData: PatchedOrgRequest) => {
  //   if (!get().token) return;
  //   set({ isOrganizationLoading: true, error: null });
  //   const response = await client.accountsOrgPartialUpdate({ body: orgData });
  //   if (response.error) {
  //     set({
  //       error: response.error,
  //       isOrganizationLoading: false,
  //     });
  //   } else {
  //     set({ channel: response.data });
  //   }
  //   set({ isOrganizationLoading: false });
  // };

  const getApiKeys = async () => {
    set({ isApiKeysLoading: true, apiKeysFetched: false, error: null });

    // unable to find this
    const response = await client.accountsApiKeysList();
    if (response.error) {
      set({ error: response.error, isApiKeysLoading: false, apiKeysFetched: true });
    } else {
      set({ apiKeys: response.data, isApiKeysLoading: false, apiKeysFetched: true });
    }
  };

  const verifyEmail = async (tokenParam: string | null, channel_id: string, user_id: string) => {
    if (!tokenParam) {
      updateRequestState('verifyEmail', {
        error: createFallbackApiError('Missing verification token.'),
        success: false,
      });
      return;
    }
    updateRequestState('verifyEmail', { loading: true, error: null, success: false });
    const response = await client.onboardVerifyRetrieve({
      path: { id: tokenParam },
      query: { channel_id: channel_id, user_id: user_id },
    });

    if (response.error) {
      updateRequestState('verifyEmail', { loading: false, error: response.error, success: false });
      return;
    }
    updateRequestState('verifyEmail', { loading: false, error: null, success: true });
  };

  const updateUserProfile = async (data: PatchedProfileUpdateRequest) => {
    updateRequestState('updateUserProfile', { loading: true, error: null, success: false });
    const response = await client.profilePartialUpdate({ body: data });
    if (response.error) {
      // updateRequestState('updateUserProfile', { loading: false, error: response.error, success: false });
      return;
    }
    const profile = Array.isArray(response.data) ? response.data[0] : response.data;
    set({
      user: profile,
      channel: profile?.channel,
    });
    updateRequestState('updateUserProfile', { loading: false, error: null, success: true });
  };

  const uploadProfilePic = async (file: File, payload?: PatchedProfileUpdateRequest) => {
    updateRequestState('updateUserProfile', { loading: true, error: null, success: false });
    const { data, error } = await client.assetsCreate({
      body: { filename: file.name },
    });
    if (error) {
      updateRequestState('updateUserProfile', {
        loading: false,
        error: error,
        success: false,
      });
      return;
    }
    const { path, signed_url } = data;
    const filePath = assets(path);
    const ctrl = new AbortController();

    void _upload({
      url: signed_url,
      file,
      signal: ctrl.signal,
      onProgress: progress => {
        console.log(progress);
      },
    })
      .then(async () => {
        await updateUserProfile({ ...payload, profile_picture: filePath });
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (error) {
          set({ error });
          return;
        }
        return { filePath, signed_url };
      })
      .catch(() => {
        updateRequestState('updateUserProfile', {
          loading: false,
          error: error,
          success: false,
        });
      });
    //   set({ isLoading: false });
  };

  const changePassword = async (data: ChangePasswordRequest) => {
    updateRequestState('changePassword', { loading: true, error: null, success: false });
    const response = await client.changePassword({ body: data });
    if (response.error) {
      // updateRequestState('changePassword', {
      //   loading: false,
      //   error: response?.error ?? null,
      //   success: false,
      // });
      // return;
    }
    updateRequestState('changePassword', { loading: false, error: null, success: true });
  };

  const getBillingHistory = async (channel_id: string) => {
    updateRequestState('getBillingHistory', { loading: true, error: null, success: false });
    const response = await client.checkoutsChannelsRetrieve({ path: { channel_id } });
    console.log(response);
    if (response.error) {
      updateRequestState('getBillingHistory', { loading: false, error: response.error, success: false });
      return;
    }
    set({ billingHistory: response.data.results });
    updateRequestState('getBillingHistory', { loading: false, error: null, success: true });
  };

  const getInvoice = async (invoiceId: string) => {
    updateRequestState('getInvoice', { loading: true, error: null, success: false });
    const response = await client.checkoutsInvoiceRetrieve({ path: { id: invoiceId } });
    if (response.error) {
      updateRequestState('getInvoice', { loading: false, error: response.error, success: false });
      return;
    }
    set({ invoice: response.data });
    updateRequestState('getInvoice', { loading: false, error: null, success: true });
  };

  const logout = () => {
    token.clear();
    set({
      user: null,
      channel: null,
      token: null,
      apiKeys: [],
      billingHistory: [],
      invoice: null,
      isAuthenticated: false,
      error: null,
      isLoading: false,
      isOrganizationLoading: false,
      isApiKeysLoading: false,
      apiKeysFetched: false,
      requests: createInitialRequestState(),
    });
  };

  return {
    user: null,
    channel: null,
    token: token.get(),
    apiKeys: [],
    billingHistory: [],
    invoice: null,
    isAuthenticated: !!token.get(),
    isLoading: false,
    isOrganizationLoading: false,
    isApiKeysLoading: false,
    apiKeysFetched: false,
    error: null,
    requests: createInitialRequestState(),

    // actions
    login,
    register,
    forgotPassword,
    resetPassword,
    getUserProfile,
    getOrganization,
    getApiKeys,
    getBillingHistory,
    getInvoice,
    verifyEmail,
    updateUserProfile,
    uploadProfilePic,
    changePassword,
    logout,
    clearRequestState: resetRequestState,
  };
});
